import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  TextField,
  Typography,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextareaAutosize,
  useTheme,
  Pagination,
  MenuItem,
  Select,
  FormControl,
  InputAdornment,
  IconButton,
  alpha
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon,
  Label as LabelIcon,
  Business as BusinessIcon,
  ExpandMore as ExpandMoreIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { httpRequest } from 'src/services/httpRequest';
import ClearIcon from '@mui/icons-material/Clear';
import PeopleIcon from '@mui/icons-material/People';
import { useSelector } from 'react-redux';
import { RootState } from 'src/redux-store/appStore';

interface VendorData {
  UserID: number;
  FirstName: string;
  LastName: string;
  PhoneNumber: string;
  EmailID: string;
  CreateDate: string;
  BidCategoryIDs: number[];
  IsPending: boolean;
  IsRejected: boolean;
  Active: boolean;
}

interface CompanyData {
  Business: string;
  FederalID: string;
  Address1: string;
  Address2?: string;
  City: string;
  State: string;
  ZipCode: string;
  TotalVendors: number | string;
  Vendors: VendorData[];
  BusinessActive: boolean;
}

interface DashboardCounts {
  TotalVendors: number;
  TotalContacts: number;
  PendingVendors: number;
  TotalCategories: number;
  ActiveContacts: number;
}

interface ApiResponse {
  status: string;
  message: string;
  data: {
    TotalRecords: number;
    TotalPages: number;
    CurrentPage: number;
    Size: string;
    Data: CompanyData | CompanyData[];
  };
}

interface CategoryResponse {
  status: string;
  message: string;
  data: {
    totalRecords: number;
    currentPage: number;
    totalPages: number;
    data: Array<{
      CategoryID: number;
      CategoryName: string;
      Active: boolean;
      RowStatus: number;
      CreateDate: string;
      CreatedBy: number;
      UpdateDate: string | null;
      UpdatedBy: number | null;
      DeleteDate: string | null;
      DeletedBy: number | null;
      TownshipID: number;
    }>;
  };
}

interface CategoryItem {
  id: number;
  name: string;
  active: boolean;
}

interface VendorStatusUpdatePayload {
  IsApproved?: boolean;
  UserIDs: number[];
  FederalID: string;
  Status: boolean;
  ReasonOfRejection?: string;
}

interface CompanyStatusDialogData {
  open: boolean;
  federalId: string;
  companyName: string;
  isActivating: boolean;
  reason: string;
}

const CompanyVendorManagement: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { state: locationState, township, uuid } = useParams();
  const [selectedStatus, setSelectedStatus] = useState<'approved' | 'pending'>(
    'approved'
  );
  const [expandedCompany, setExpandedCompany] = useState<string | false>(false);
  const [expandedVendor, setExpandedVendor] = useState<string | false>(false);

  const [companiesData, setCompaniesData] = useState<CompanyData[]>([]);
  const [dashboardCounts, setDashboardCounts] = useState<DashboardCounts>({
    TotalVendors: 0,
    ActiveContacts: 0,
    PendingVendors: 0,
    TotalCategories: 0,
    TotalContacts: 0
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(25);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [active, setActive] = useState<boolean>(true);
  const [categories, setCategories] = useState<CategoryItem[]>([]);
  const [loadingCategories, setLoadingCategories] = useState<boolean>(false);
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);

  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  const userRole = userDetails.UserRole || '';

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning'
  });

  const [rejectionDialog, setRejectionDialog] = useState({
    open: false,
    vendorId: 0,
    federalId: '',
    reason: ''
  });

  const [resetPasswordDialog, setResetPasswordDialog] = useState<{
    open: boolean;
    vendorId: number | null;
    vendorName: string;
    newPassword: string;
    loading: boolean;
    error: string | null;
  }>({
    open: false,
    vendorId: null,
    vendorName: '',
    newPassword: '',
    loading: false,
    error: null
  });

  const [companyStatusDialog, setCompanyStatusDialog] =
    useState<CompanyStatusDialogData>({
      open: false,
      federalId: '',
      companyName: '',
      isActivating: false,
      reason: ''
    });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const status = params.get('selected') as 'approved' | 'pending';
    const federalId = params.get('federal') as string;
    const userId = params.get('user') as string;

    // Handle status change
    if (status && status !== selectedStatus) {
      setSelectedStatus(status);
      setActive(status === 'approved');
    }

    // Handle federalId and expand the corresponding company accordion
    if (federalId) {
      setExpandedCompany(federalId);
    } else {
      setExpandedCompany(false);
    }

    // Handle userId and expand the corresponding vendor accordion
    if (userId) {
      setExpandedVendor(userId);
    } else {
      setExpandedVendor(false);
    }
  }, [location.search]); // This effect runs when the URL changes
  const handleStatusChange = (
    _event: React.MouseEvent<HTMLElement>,
    newStatus: 'approved' | 'pending'
  ) => {
    if (newStatus !== selectedStatus) {
      setSelectedStatus(newStatus); // Set new selectedStatus
      setActive(newStatus === 'approved'); // Update the active state based on newStatus
      setPage(1); // Reset pagination when status changes

      // Update the URL search params
      navigate(
        {
          pathname: location.pathname,
          search: `?selected=${newStatus}&federal=${new URLSearchParams(location.search).get('federal') || ''
            }&user=${new URLSearchParams(location.search).get('user') || ''}`
        },
        { replace: true } // Use replace so we don't create a new history entry
      );
    }
  };

  // Debounce the search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // Debounce delay (500ms)

    return () => {
      clearTimeout(timer); // Clear the previous timer on each change
    };
  }, [searchTerm]);

  const fetchCategories = async () => {
    setLoadingCategories(true);
    try {
      const response = (await httpRequest(
        'GET',
        `/bid_portal/category/get_all`
      )) as CategoryResponse;

      if (
        response.status === 'success' &&
        response.data &&
        response.data.data
      ) {
        const allCategories = response.data.data.map((cat) => ({
          id: cat.CategoryID,
          name: cat.CategoryName,
          active: cat.Active
        }));

        setCategories(allCategories);
      } else {
        throw new Error(response.message || 'Failed to retrieve categories');
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoadingCategories(false);
    }
  };

  const getCategoryNameById = (categoryId: number): string => {
    const category = categories.find((cat) => cat.id === categoryId);
    return category ? category.name : `Category ${categoryId}`;
  };

  const fetchDashboardCounts = async () => {
    try {
      const response = await httpRequest('GET', '/auth/dashboard_counts');
      if (response && response.status === 'success' && response.data) {
        setDashboardCounts(response.data);
      }
    } catch (error) {
      console.error('Error fetching dashboard counts:', error);
    }
  };

  const fetchCompaniesData = async () => {
    setLoading(true);
    try {
      const response: ApiResponse = await httpRequest(
        'GET',
        `/auth/getAll?Page=${page}&Size=${pageSize}&Active=${active}${debouncedSearchTerm ? `&Search=${debouncedSearchTerm}` : ''
        }`
      );

      if (response && response.status === 'success' && response.data) {
        if (Array.isArray(response.data.Data)) {
          setCompaniesData(response.data.Data);
        } else {
          setCompaniesData([response.data.Data]);
        }

        // Update pagination info
        setTotalPages(response.data.TotalPages || 1);
        setTotalRecords(response.data.TotalRecords || 0);

        if (response.data.CurrentPage && response.data.CurrentPage !== page) {
          setPage(response.data.CurrentPage);
        }
      }
    } catch (error) {
      console.error('Error fetching companies data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchDashboardCounts();
  }, []);

  useEffect(() => {
    fetchCompaniesData();
  }, [selectedStatus, active, page, pageSize, debouncedSearchTerm]);

  const updateVendorApproval = async (
    vendorId: number,
    federalId: string,
    isApproved: boolean
  ) => {
    setLoading(true);
    try {
      const payload: VendorStatusUpdatePayload = {
        IsApproved: isApproved,
        UserIDs: [vendorId],
        FederalID: federalId,
        Status: true,
        ...(isApproved === false && {
          ReasonOfRejection: rejectionDialog.reason
        })
      };

      const response = await httpRequest(
        'PATCH',
        '/auth/vendors/status',
        payload
      );

      if (response && response.status === 'success') {
        setSnackbar({
          open: true,
          message: `Vendor ${isApproved ? 'approved' : 'rejected'
            } successfully`,
          severity: 'success'
        });

        fetchDashboardCounts();
        fetchCompaniesData();
      } else {
        throw new Error(response?.message || 'Unknown error occurred');
      }
    } catch (error) {
      console.error('Error updating vendor approval:', error);
      setSnackbar({
        open: true,
        message: `Failed to update vendor status: ${error instanceof Error ? error.message : 'Unknown error'
          }`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const updateVendorStatus = async (
    vendorId: number,
    federalId: string,
    isActivating: boolean
  ) => {
    setLoading(true);
    try {
      const payload: VendorStatusUpdatePayload = {
        UserIDs: [vendorId],
        FederalID: federalId,
        Status: isActivating
      };

      const response = await httpRequest(
        'PATCH',
        '/auth/vendors/status',
        payload
      );

      if (response && response.status === 'success') {
        setSnackbar({
          open: true,
          message: `Vendor ${isActivating ? 'activated' : 'deactivated'
            } successfully`,
          severity: 'success'
        });

        fetchDashboardCounts();
        fetchCompaniesData();
      } else {
        throw new Error(response?.message || 'Unknown error occurred');
      }
    } catch (error) {
      console.error('Error updating vendor status:', error);
      setSnackbar({
        open: true,
        message: `Failed to update vendor status: ${error instanceof Error ? error.message : 'Unknown error'
          }`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const updateCompanyStatus = async (
    federalId: string,
    isActivating: boolean,
    vendorIds: number[]
  ) => {
    setLoading(true);
    try {
      const payload = isActivating
        ? {
          // IsApproved: true,
          UserIDs: vendorIds,
          FederalID: federalId,
          Status: true
        }
        : {
          UserIDs: vendorIds,
          FederalID: federalId,
          Status: false
        };

      const response = await httpRequest(
        'PATCH',
        '/auth/vendors/status',
        payload
      );

      if (response && response.status === 'success') {
        setSnackbar({
          open: true,
          message: `Company ${isActivating ? 'activated' : 'deactivated'
            } successfully`,
          severity: 'success'
        });

        fetchDashboardCounts();
        fetchCompaniesData();
      } else {
        throw new Error(response?.message || 'Unknown error occurred');
      }
    } catch (error) {
      console.error('Error updating company status:', error);
      setSnackbar({
        open: true,
        message: `Failed to update company status: ${error instanceof Error ? error.message : 'Unknown error'
          }`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleApproveVendor = (vendorId: number, federalId: string) => {
    updateVendorApproval(vendorId, federalId, true);
  };

  const openRejectionDialog = (vendorId: number, federalId: string) => {
    setRejectionDialog({
      open: true,
      vendorId,
      federalId,
      reason: ''
    });
  };

  const handleEditVendor = (vendorId: number) => {
    navigate(
      `/${locationState}/${township}/${uuid}/bid-portal/Admin/vendors/edit/${vendorId}`
    );
  };

  const handleRejectVendor = () => {
    const { vendorId, federalId } = rejectionDialog;
    updateVendorApproval(vendorId, federalId, false);
    setRejectionDialog({ ...rejectionDialog, open: false });
  };

  const generateRandomPassword = () => {
    const length = 12;
    const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lower = 'abcdefghijklmnopqrstuvwxyz';
    const digits = '0123456789';
    const special = '@#$!%*?&';
    const all = upper + lower + digits + special;

    const pick = (chars: string) =>
      chars[Math.floor(Math.random() * chars.length)];

    let password =
      pick(upper) + pick(lower) + pick(digits) + pick(special);

    // eslint-disable-next-line no-plusplus
    for (let i = password.length; i < length; i++) {
      password += pick(all);
    }

    // Shuffle the characters
    password = password
      .split('')
      .sort(() => Math.random() - 0.5)
      .join('');

    return password;
  };

  const openResetPasswordDialog = (vendor: VendorData) => {
    setResetPasswordDialog({
      open: true,
      vendorId: vendor.UserID,
      vendorName: `${vendor.FirstName} ${vendor.LastName}`,
      newPassword: generateRandomPassword(),
      loading: false,
      error: null
    });
  };

  const closeResetPasswordDialog = () => {
    setResetPasswordDialog((prev) => ({ ...prev, open: false }));
  };

  const handlePasswordFieldChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { value } = event.target;
    setResetPasswordDialog((prev) => ({ ...prev, newPassword: value }));
  };

  const validatePassword = (password: string): string | null => {
    if (!password) {
      return 'Password is required.';
    }

    if (password.length < 8) {
      return 'Password must be at least 8 characters long.';
    }

    if (password.length > 100) {
      return 'Password cannot exceed 100 characters.';
    }

    const pattern =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@#$!%*?&])[A-Za-z\d@#$!%*?&]+$/;

    if (!pattern.test(password)) {
      return 'Password must contain at least one number, one uppercase letter, one lowercase letter, and one special character.';
    }

    return null;
  };

  const submitResetPassword = async () => {
    const { vendorId, newPassword } = resetPasswordDialog;

    if (!vendorId) return;

    const validationError = validatePassword(newPassword);

    if (validationError) {
      setResetPasswordDialog((prev) => ({
        ...prev,
        error: validationError
      }));
      return;
    }

    try {
      setResetPasswordDialog((prev) => ({
        ...prev,
        loading: true,
        error: null
      }));

      const response = await httpRequest(
        'PATCH',
        `/auth/admin/vendor/${vendorId}/password`,
        { Password: newPassword }
      );

      if (!response || response.status !== 'success') {
        throw new Error(response?.message || 'Failed to update password');
      }

      setSnackbar({
        open: true,
        message: 'Vendor password updated successfully.',
        severity: 'success'
      });

      closeResetPasswordDialog();
    } catch (error: any) {
      setResetPasswordDialog((prev) => ({
        ...prev,
        error:
          error?.message ||
          'An error occurred while updating vendor password.'
      }));
    } finally {
      setResetPasswordDialog((prev) => ({ ...prev, loading: false }));
    }
  };

  const openCompanyStatusDialog = (
    event: React.MouseEvent<HTMLButtonElement>,
    company: CompanyData,
    isActivating: boolean
  ) => {
    event.stopPropagation();
    setCompanyStatusDialog({
      open: true,
      federalId: company.FederalID,
      companyName: company.Business,
      isActivating,
      reason: ''
    });
  };

  const handleCompanyStatusChange = () => {
    const { federalId, isActivating } = companyStatusDialog;

    const company = companiesData.find((c) => c.FederalID === federalId);

    if (company) {
      const vendorIds = company.Vendors.map((vendor) => vendor.UserID);
      updateCompanyStatus(federalId, isActivating, vendorIds);
    }

    setCompanyStatusDialog({ ...companyStatusDialog, open: false });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const stats = [
    {
      title: 'Total Vendors',
      count: dashboardCounts.TotalVendors,
      icon: <BusinessIcon color="primary" />
    },
    {
      title: 'Total Contacts',
      count: dashboardCounts.TotalContacts,
      icon: <PeopleIcon color="info" />
    },
    {
      title: 'Active Contacts',
      count: dashboardCounts.ActiveContacts,
      icon: <CheckCircleIcon color="success" />
    },
    {
      title: 'Pending Review',
      count: dashboardCounts.PendingVendors,
      icon: <AccessTimeIcon color="warning" />
    },
    {
      title: 'Categories',
      count: dashboardCounts.TotalCategories,
      icon: <LabelIcon sx={{ color: 'purple' }} />
    }
  ];

  const handleCompanyAccordionChange =
    (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedCompany(isExpanded ? panel : false);

      // Update the URL search params for company expansion
      navigate(
        {
          pathname: location.pathname,
          search: `?selected=${selectedStatus}&federal=${isExpanded ? panel : ''
            }&user=${new URLSearchParams(location.search).get('user') || ''}`
        },
        { replace: true } // Use replace so we don't create a new history entry
      );
    };

  const handleVendorAccordionChange =
    (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedVendor(isExpanded ? panel : false);

      // Update the URL search params for vendor expansion
      navigate(
        {
          pathname: location.pathname,
          search: `?selected=${selectedStatus}&federal=${new URLSearchParams(location.search).get('federal') || ''
            }&user=${isExpanded ? panel : ''}`
        },
        { replace: true } // Use replace so we don't create a new history entry
      );
    };

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
    setExpandedCompany(false);
    setExpandedVendor(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePageSizeChange = (event: any) => {
    setPageSize(Number(event.target.value));
    setPage(1);
  };

  const filteredCompanies = companiesData
    .map((company) => ({
      ...company,
      Vendors: company.Vendors.filter((vendor) =>
        selectedStatus === 'approved'
          ? !vendor.IsPending && !vendor.IsRejected
          : vendor.IsPending || vendor.IsRejected
      )
    }))
    .filter((company) => company.Vendors.length > 0);

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 'bold',
            color: theme.palette.primary.main,
            position: 'relative',
            '&:after': {
              content: '""',
              position: 'absolute',
              bottom: -8,
              left: 0,
              width: 65,
              height: 4,
              borderRadius: 2,
              backgroundColor: theme.palette.primary.main
            }
          }}
        >
          Vendors
        </Typography>

        {userRole === 'Admin' && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{ bgcolor: 'black' }}
            onClick={() =>
              navigate(
                `/${locationState}/${township}/${uuid}/bid-portal/Admin/vendors/registration`
              )
            }
          >
            Add Vendor
          </Button>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <TextField
          placeholder="Search vendors or contacts.."
          variant="outlined"
          size="small"
          value={searchTerm}
          sx={{ flexGrow: 1 }}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchTerm('')}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
            sx: {
              borderRadius: 1,
              bgcolor: alpha(theme.palette.background.default, 0.7)
            }
          }}
        />
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {stats.map((stat) => (
          <Grid item xs={12} sm={6} md={2.4} key={stat.title}>
            <Card>
              <CardContent>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Box>
                    <Typography color="text.secondary" variant="body2">
                      {stat.title}
                    </Typography>
                    <Typography variant="h4" component="div">
                      {stat.count}
                    </Typography>
                  </Box>
                  <Box sx={{ p: 1, borderRadius: 1 }}>{stat.icon}</Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <ToggleButtonGroup
        value={selectedStatus}
        exclusive
        onChange={handleStatusChange}
        sx={{ mb: 3 }}
      >
        <ToggleButton value="approved">
          <CheckCircleIcon sx={{ mr: 1 }} />
          Approved
        </ToggleButton>
        <ToggleButton value="pending">
          <AccessTimeIcon sx={{ mr: 1 }} />
          Pending & Rejected
        </ToggleButton>
      </ToggleButtonGroup>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Stack spacing={2}>
            {filteredCompanies.length > 0 ? (
              filteredCompanies.map((company) => (
                <Paper key={company.FederalID} elevation={1}>
                  <Accordion
                    expanded={expandedCompany === company.FederalID}
                    onChange={handleCompanyAccordionChange(company.FederalID)}
                    sx={{
                      '&:before': {
                        display: 'none'
                      },
                      borderRadius: 1
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        backgroundColor: 'background.default',
                        borderBottom: '1px solid',
                        borderColor: 'divider'
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          width: '100%',
                          alignItems: 'center'
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            flex: 1
                          }}
                        >
                          <BusinessIcon color="primary" />
                          <Box>
                            <Typography variant="h6">
                              {company.Business}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {company.Address1}, {company.City},{' '}
                              {company.State} {company.ZipCode}
                            </Typography>
                          </Box>
                        </Box>
                        {userRole === 'Admin' && (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 2,
                              flexWrap: 'wrap',
                              justifyContent: 'flex-end',
                              flex: 1,
                              minWidth: '180px',
                              maxWidth: '350px',
                              mt: { xs: 1, sm: 0 }
                            }}
                          >
                            {active &&
                              (company.BusinessActive ? (
                                <Button
                                  variant="contained"
                                  color="error"
                                  size="small"
                                  onClick={(e) =>
                                    openCompanyStatusDialog(e, company, false)
                                  }
                                  startIcon={<CancelIcon />}
                                  sx={{
                                    minWidth: '100px',
                                    height: '36px',
                                    fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                  }}
                                >
                                  Mark as inactive
                                </Button>
                              ) : (
                                <Button
                                  variant="contained"
                                  color="primary"
                                  size="small"
                                  onClick={(e) =>
                                    openCompanyStatusDialog(e, company, true)
                                  }
                                  startIcon={<CheckCircleIcon />}
                                  sx={{
                                    minWidth: '100px',
                                    height: '36px',
                                    fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                  }}
                                >
                                  Mark as active
                                </Button>
                              ))}
                            <Chip
                              label={`${company.TotalVendors} Contacts`}
                              color="primary"
                              size="small"
                              sx={{
                                minWidth: '100px',
                                fontSize: { xs: '0.75rem', sm: '0.875rem' }
                              }}
                            />
                          </Box>
                        )}
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <Stack spacing={1}>
                        {company.Vendors.map((vendor) => (
                          <Paper key={vendor.UserID} elevation={1}>
                            <Accordion
                              expanded={
                                expandedVendor === vendor.UserID.toString()
                              }
                              onChange={handleVendorAccordionChange(
                                vendor.UserID.toString()
                              )}
                              sx={{
                                '&:before': {
                                  display: 'none'
                                },
                                backgroundColor: 'white'
                              }}
                            >
                              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    width: '100%',
                                    alignItems: 'center'
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 1
                                    }}
                                  >
                                    <Typography variant="subtitle1">
                                      {vendor.FirstName} {vendor.LastName}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      {new Date(
                                        vendor.CreateDate
                                      ).toLocaleDateString()}
                                    </Typography>
                                  </Box>
                                  <Box>
                                    <Chip
                                      label={
                                        vendor.IsPending
                                          ? 'Pending'
                                          : vendor.IsRejected
                                            ? 'Rejected'
                                            : vendor.Active
                                              ? 'Active'
                                              : 'Inactive'
                                      }
                                      color={
                                        vendor.IsPending
                                          ? 'warning'
                                          : vendor.IsRejected
                                            ? 'error'
                                            : vendor.Active
                                              ? 'success'
                                              : 'default'
                                      }
                                      size="small"
                                    />
                                  </Box>
                                </Box>
                              </AccordionSummary>
                              <AccordionDetails>
                                <Stack spacing={1}>
                                  <Typography>
                                    Email: {vendor.EmailID}
                                  </Typography>
                                  <Typography>
                                    Phone: {vendor.PhoneNumber}
                                  </Typography>
                                  <Typography>
                                    Registration:{' '}
                                    {new Date(
                                      vendor.CreateDate
                                    ).toLocaleDateString()}
                                  </Typography>
                                  <Box>
                                    <Typography sx={{ mb: 1 }}>
                                      Bid Categories
                                    </Typography>
                                    {loadingCategories ? (
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center'
                                        }}
                                      >
                                        <CircularProgress
                                          size={16}
                                          sx={{ mr: 1 }}
                                        />
                                        <Typography variant="body2">
                                          Loading categories...
                                        </Typography>
                                      </Box>
                                    ) : (
                                      <Stack
                                        direction="row"
                                        spacing={1}
                                        flexWrap="wrap"
                                      >
                                        {vendor.BidCategoryIDs.map(
                                          (categoryId) => (
                                            <Chip
                                              key={categoryId}
                                              label={getCategoryNameById(
                                                categoryId
                                              )}
                                              size="small"
                                              sx={{ mb: 1 }}
                                            />
                                          )
                                        )}
                                      </Stack>
                                    )}
                                  </Box>

                                  {userRole === 'Admin' && (
                                    <Box sx={{ mt: 2 }}>
                                      <Button
                                        color="primary"
                                        variant="contained"
                                        size="small"
                                        startIcon={<EditIcon />}
                                        sx={{ m: 0.5 }}
                                        onClick={() =>
                                          handleEditVendor(vendor.UserID)
                                        }
                                      >
                                        Edit
                                      </Button>

                                      {vendor.IsPending || vendor.IsRejected ? (
                                        <>
                                          <Button
                                            color="success"
                                            variant="contained"
                                            size="small"
                                            sx={{ m: 0.5 }}
                                            onClick={() =>
                                              handleApproveVendor(
                                                vendor.UserID,
                                                company.FederalID
                                              )
                                            }
                                          >
                                            Approve
                                          </Button>
                                          {vendor.IsPending && (
                                            <Button
                                              color="error"
                                              variant="contained"
                                              size="small"
                                              onClick={() =>
                                                openRejectionDialog(
                                                  vendor.UserID,
                                                  company.FederalID
                                                )
                                              }
                                              sx={{ m: 0.5 }}
                                            >
                                              Reject
                                            </Button>
                                          )}
                                        </>
                                      ) : (
                                        <>
                                          {!vendor.Active ? (
                                            <Button
                                              color="success"
                                              variant="contained"
                                              size="small"
                                              onClick={() =>
                                                updateVendorStatus(
                                                  vendor.UserID,
                                                  company.FederalID,
                                                  true
                                                )
                                              }
                                            >
                                              Mark as active
                                            </Button>
                                          ) : (
                                            <Button
                                              color="error"
                                              variant="contained"
                                              size="small"
                                              onClick={() =>
                                                updateVendorStatus(
                                                  vendor.UserID,
                                                  company.FederalID,
                                                  false
                                                )
                                              }
                                            >
                                              Mark as inactive
                                            </Button>
                                          )}
                                        </>
                                      )}
                                      {!vendor.IsPending &&
                                        !vendor.IsRejected &&
                                        vendor.Active && (
                                          <Button
                                            color="secondary"
                                            variant="contained"
                                            size="small"
                                            sx={{ m: 0.5 }}
                                            onClick={() =>
                                              openResetPasswordDialog(vendor)
                                            }
                                          >
                                            Change / Generate Password
                                          </Button>
                                        )}
                                    </Box>
                                  )}
                                </Stack>
                              </AccordionDetails>
                            </Accordion>
                          </Paper>
                        ))}
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                </Paper>
              ))
            ) : (
              <Typography variant="body1" textAlign="center" py={4}>
                No companies or vendors found matching the current filter.
              </Typography>
            )}
          </Stack>

          {/* Pagination Controls */}
          {filteredCompanies.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: { xs: 'center', sm: 'space-between' },
                alignItems: 'center',
                mt: 4,
                flexWrap: 'wrap',
                gap: 2
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Rows per page:
                </Typography>
                <FormControl size="small">
                  <Select
                    value={pageSize}
                    onChange={handlePageSizeChange}
                    sx={{ minWidth: 80 }}
                  >
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                    <MenuItem value={100}>100</MenuItem>
                  </Select>
                </FormControl>
                <Typography variant="body2" color="text.secondary">
                  Showing {Math.min((page - 1) * pageSize + 1, totalRecords)} -{' '}
                  {Math.min(page * pageSize, totalRecords)} of {totalRecords}
                </Typography>
              </Box>

              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
                siblingCount={1}
                boundaryCount={1}
              />
            </Box>
          )}
        </>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={rejectionDialog.open}
        onClose={() => setRejectionDialog({ ...rejectionDialog, open: false })}
      >
        <DialogTitle>Reason for Rejection</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Please provide a reason for rejecting this vendor:
          </DialogContentText>
          <TextareaAutosize
            minRows={3}
            style={{ width: '100%', marginTop: '16px', padding: '8px' }}
            value={rejectionDialog.reason}
            onChange={(e) =>
              setRejectionDialog({ ...rejectionDialog, reason: e.target.value })
            }
            placeholder="Enter rejection reason..."
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setRejectionDialog({ ...rejectionDialog, open: false })
            }
          >
            Cancel
          </Button>
          <Button
            onClick={handleRejectVendor}
            color="error"
            variant="contained"
            disabled={!rejectionDialog.reason.trim()}
          >
            Reject Vendor
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={companyStatusDialog.open}
        onClose={() =>
          setCompanyStatusDialog({ ...companyStatusDialog, open: false })
        }
      >
        <DialogTitle>
          {companyStatusDialog.isActivating ? 'Activate' : 'Deactivate'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {companyStatusDialog.isActivating
              ? `Are you sure you want to activate all contacts for ${companyStatusDialog.companyName}?`
              : `Are you sure you want to deactivate all contacts for ${companyStatusDialog.companyName}?`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setCompanyStatusDialog({ ...companyStatusDialog, open: false })
            }
          >
            Cancel
          </Button>
          <Button
            onClick={handleCompanyStatusChange}
            color={companyStatusDialog.isActivating ? 'success' : 'error'}
            variant="contained"
          >
            {companyStatusDialog.isActivating ? 'Activate' : 'Deactivate'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={resetPasswordDialog.open}
        onClose={closeResetPasswordDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Update Vendor Password</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Set or generate a new password for{' '}
            {resetPasswordDialog.vendorName}.<br/>
            Password must be at least 8
            characters long and include at least one number, one uppercase
            letter, one lowercase letter, and one special character.
          </DialogContentText>

          {resetPasswordDialog.error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {resetPasswordDialog.error}
            </Alert>
          )}

          <TextField
            fullWidth
            margin="dense"
            label="Password"
            type="text"
            value={resetPasswordDialog.newPassword}
            onChange={handlePasswordFieldChange}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="Generate random password"
                    onClick={() =>
                      setResetPasswordDialog((prev) => ({
                        ...prev,
                        newPassword: generateRandomPassword(),
                        error: null
                      }))
                    }
                    edge="end"
                  >
                    <RefreshIcon />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={closeResetPasswordDialog}
            disabled={resetPasswordDialog.loading}
          >
            Cancel
          </Button>
          <Button
            onClick={submitResetPassword}
            color="primary"
            variant="contained"
            disabled={resetPasswordDialog.loading}
          >
            {resetPasswordDialog.loading ? (
              <CircularProgress size={20} />
            ) : (
              'Update Password'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CompanyVendorManagement;
